import { Platform } from 'react-native';
import { createLogger } from '@/lib/logger';
import * as FileSystem from 'expo-file-system/legacy';
import { supabase } from './supabase';

const log = createLogger('UPLOAD');

export async function uploadVideo(uri: string, userId: string): Promise<string> {
  // Check file size before attempting upload (50MB limit)
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
  try {
    const fileInfo = await FileSystem.getInfoAsync(uri);
    if (fileInfo.exists && fileInfo.size && fileInfo.size > MAX_VIDEO_SIZE) {
      throw new Error(`Video is too large (${Math.round(fileInfo.size / 1024 / 1024)}MB). Maximum size is 50MB. Try a shorter clip.`);
    }
  } catch (sizeErr: any) {
    if (sizeErr.message?.includes('too large')) throw sizeErr;
    // Can't check size — proceed and let server reject if too big
  }

  const fileExt = uri.split('.').pop();
  const fileName = `${userId}/${Date.now()}.${fileExt}`;

  let arrayBuffer: ArrayBuffer;

  try {
    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();

      if (typeof blob.arrayBuffer === 'function') {
        arrayBuffer = await blob.arrayBuffer();
      } else {
        arrayBuffer = await new Response(blob).arrayBuffer();
      }
    } else {
      const base64 = await FileSystem.readAsStringAsync(uri, {
        encoding: 'base64',
      });

      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      arrayBuffer = bytes.buffer;
    }
  } catch (conversionError: any) {
    log.error('Video conversion error:', conversionError);
    throw new Error(`Failed to convert video: ${conversionError.message}`);
  }

  const { data, error } = await supabase.storage
    .from('workout-media')
    .upload(fileName, arrayBuffer, {
      contentType: 'video/mp4',
      upsert: false,
    });

  if (error) {
    log.error('Storage upload error:', error);
    if (error.message?.includes('maximum allowed size') || error.message?.includes('Payload too large')) {
      throw new Error('Video is too large to upload. Try a shorter clip (10-15 seconds recommended).');
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      throw new Error('Video upload failed — check your connection and try again.');
    } else {
      throw new Error(`Video upload failed: ${error.message}`);
    }
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from('workout-media').getPublicUrl(data.path);

  return publicUrl;
}
